import { ReactNode, MouseEvent, memo } from 'react'

import Linkify from 'react-linkify'
import { useTranslation } from 'react-i18next'

import Dropdown from 'common/components/dropdown'
import DateHelper from 'common/helpers/date'
import Icons from 'common/components/icons'
import Avatar from 'common/components/avatar'
import { MessageWithReply, MessageOrigin } from 'modules/chat/types'

import '@hotmart/cosmos/dist/styles/utilities/spacing/spacing.css'

export interface IProps {
  message: MessageWithReply,
  active?: boolean,
  onReplyRequest?: (message: MessageWithReply) => void,
  onReplyClick?: (event: MouseEvent, messageId: string) => void,
  userPhoto?: string,
  style?: Record<string, unknown>,
  isMobile?: boolean
}

const linkDecorator = (href: string, text: string, key: number) => (
  <a href={href} key={key} target="_blank" rel="noreferrer">{text}</a>
)

const ChatMessage = ({
  message, active, onReplyRequest, onReplyClick, userPhoto, style, isMobile
}: IProps) => {
  const { t } = useTranslation()

  const time = DateHelper.formatTimestampToTime(message.createdAt)

  const reply = () => {
    onReplyRequest?.(message)
  }

  const replyClick = (event: MouseEvent) => {
    message.messageOrigin && onReplyClick?.(event, message.messageOrigin.messageId)
  }

  const renderUserIcon = (isMessageOrigin?:boolean) => {
    return !isMessageOrigin && (
      <Avatar
        alt={message.name}
        color={message.color}
        src={userPhoto || message.photo}/>
    )
  }

  const renderDropdownActions = (label: ReactNode) => {
    return (
      <Dropdown className="message-actions">
        <div slot="button">
          {label}
        </div>
        <Dropdown.Item className="message-actions-item" onClick={reply}>
          <div className="_d-flex">
            <Icons prefix="far" iconName="reply"/>
            <span className="action-label _ml-2">
              {t('chat.actions.reply')}
            </span>
          </div>
        </Dropdown.Item>
      </Dropdown>
    )
  }

  const renderHeader = (message: MessageOrigin, isMessageOrigin?:boolean) => {
    return (
      <div className="messages-header">
        { renderUserIcon(isMessageOrigin) }
        <span className="title">{message.name}</span>
        {
          !isMessageOrigin
          && <time dateTime={time}>{time}</time>
        }
        {
          message.owner && !isMessageOrigin
          && (
            <div className="ballon">
              <span>{t('chat.moderator')}</span>
            </div>
          )
        }
        { !isMessageOrigin && renderDropdownActions(<Icons prefix="fas" iconName="ellipsis-h"/>) }
      </div>
    )
  }

  const renderReplyMessage = () => {
    if (!message.messageOrigin) {
      return null
    }

    return (
      <div id={message.messageId}>
        <div className="reply-container cursor" onClick={replyClick}>
          <a href={`#${message.messageOrigin.messageId}`}>
            {renderHeader(message.messageOrigin, true)}
            <div className="messages-content _m-0">
              <span>{message.messageOrigin.message}</span>
            </div>
          </a>
        </div>
      </div>
    )
  }

  const renderMessageMobile = (isMessageOrigin?:boolean) => {
    return (
      <>
        <div className="icon">
          { renderUserIcon(isMessageOrigin) }
        </div>
        <div className="content-message">
          <time dateTime={time}>{time}</time>
          {
            message.owner && !isMessageOrigin
            && (
              <div className="ballon">
                <span>{t('chat.moderator')}</span>
              </div>
            )
          }
          { renderDropdownActions(<span className="title"> {message.name}: </span>)}
          { message.messageOrigin
            && <div className="reply-mobile"> {renderReplyMessage()} </div>}
          {
            message.owner
              ? (
                <Linkify componentDecorator={linkDecorator}>
                  <span className="message">{message.message}</span>
                </Linkify>
              )
              : <span className="message">{message.message}</span>
          }
        </div>
      </>
    )
  }

  return (
    <div className="content" style={style}>
      {
        isMobile ? renderMessageMobile()
          : (
            <div className={`message ${active ? 'active' : ''}`} id={message.messageId}>
              {renderHeader(message)}
              {message.messageOrigin && renderReplyMessage()}
              <div className="messages-content">
                {
                  message.owner
                    ? (
                      <Linkify componentDecorator={linkDecorator}>
                        <span>{message.message}</span>
                      </Linkify>
                    )
                    : <span>{message.message}</span>
                }
              </div>
            </div>
          )
      }
    </div>
  )
}

export default memo(ChatMessage)
